import { SUBSCRIPTION_OVER_POPUP } from 'constants/sidebars';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from 'store/actionCreators/modal';
import { getAuthUser } from 'store/selectors/auth';
import {
  getCommunities,
  getCommunitiesLoaded,
} from 'store/selectors/communities';
import { modalSelector } from 'store/selectors/modal';
import {
  selectCurrentUserSubscription,
  selectCurrentUserSubscriptionLoaded,
  selectIsSubscriptionRefreshing,
} from 'store/selectors/subscriptions';

const shownStorageKey = (userId: string) =>
  `gumb:startOnboardingShown:${userId}`;

const hasShownForUser = (userId: string) => {
  try {
    return localStorage.getItem(shownStorageKey(userId)) === '1';
  } catch {
    return false;
  }
};

const markShownForUser = (userId: string) => {
  try {
    localStorage.setItem(shownStorageKey(userId), '1');
  } catch {
    // Private mode can block storage. The ref still stops a repeat in this mount.
  }
};

/**
 * Opens "How would you like to start?" for a signed-in user with no
 * subscription and no communities. Same popup as the sidebar + button.
 * Once per user, remembered in localStorage, after both lists have loaded.
 */
export const useAutoOpenStartPopup = () => {
  const dispatch = useDispatch();
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const communitiesLoaded = useSelector(getCommunitiesLoaded);
  const subscription = useSelector(selectCurrentUserSubscription);
  const subscriptionLoaded = useSelector(selectCurrentUserSubscriptionLoaded);
  const subscriptionRefreshing = useSelector(selectIsSubscriptionRefreshing);
  const modal = useSelector(modalSelector);
  const decidedRef = useRef(false);

  useEffect(() => {
    const userId = profile?.userId;
    if (!userId || decidedRef.current) return;
    if (!communitiesLoaded || !subscriptionLoaded || subscriptionRefreshing) {
      return;
    }

    const hasNoPlan = !subscription?.id;
    const hasNoCommunity = communities.length === 0;
    if (!hasNoPlan || !hasNoCommunity || hasShownForUser(userId)) {
      decidedRef.current = true;
      return;
    }

    // Another dialog is already up (including this popup from the + button).
    if (modal?.isOpen) {
      decidedRef.current = true;
      markShownForUser(userId);
      return;
    }

    decidedRef.current = true;
    markShownForUser(userId);
    dispatch(
      openModal({
        type: SUBSCRIPTION_OVER_POPUP,
        data: { variant: 'noActivePlan' },
      }),
    );
  }, [
    communities.length,
    communitiesLoaded,
    dispatch,
    modal?.isOpen,
    profile?.userId,
    subscription?.id,
    subscriptionLoaded,
    subscriptionRefreshing,
  ]);
};
